// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { PageContent } from '@/components/page-content';
import { StatusChip } from '@/components/status-chip';
import {
  ArrowForward,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  EntityWatch,
  MessageChannel,
  MessageTypeCatalogEntry,
  messageChannelSchema,
  toMessageChannelLabel,
} from '@/notification/api/notification-types';
import {
  useMessageTypes,
  useNotificationSubscriptions,
  useSaveNotificationSubscriptions,
} from '@/notification/hooks/use-notification-subscriptions';
import { toEntityTypeLabel } from '@/notification/utilities/entity-type-label';
import { MEDIUM_DATE_FORMAT } from '@/utilities/date-time';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { FormEvent, Fragment, useState } from 'react';

// Every channel a message type can declare, in the order the columns read
const CHANNEL_COLUMNS: MessageChannel[] = [...messageChannelSchema.options];
const GRID_TEMPLATE_COLUMNS = `minmax(280px, 1fr) repeat(${CHANNEL_COLUMNS.length}, 72px)`;
const COLUMN_HEADING_FONT_SIZE = '12px';
const COLUMN_HEADING_PADDING_Y = '15px';

/** One row per message type, keyed by type, holding the channels chosen. */
type SubscriptionDraft = Map<string, Set<MessageChannel>>;

// Watching one record is built but not being offered yet, so the card stays out
// of the page until the feature ships rather than being deleted and rewritten.
const IS_RECORD_WATCHING_READY = false;

export function NotificationSubscriptionsPage() {
  return (
    <PageContent>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SettingsOutlined fontSize="small" color="action" />
          <Typography variant="h6">Notification subscriptions</Typography>
        </Stack>
        <CustomNotificationsCallout />
        <Stack spacing={3}>
          <EventSubscriptionsCard />
          {IS_RECORD_WATCHING_READY && <RecordNotificationsCard />}
        </Stack>
      </Stack>
    </PageContent>
  );
}

/** Points at the organization screen, which is where a shared address is set. */
function CustomNotificationsCallout() {
  return (
    <RouterLink
      to="/organization"
      hash="custom-notifications"
      // The whole strip is the target, so the words keep the alert's own text
      // colour rather than a link colour matching neither the icon nor the
      // arrow beside them
      style={{ color: 'inherit', display: 'block', textDecoration: 'none' }}
    >
      <Alert
        severity="info"
        sx={{
          cursor: 'pointer',
          width: '100%',
          '& .MuiAlert-message': { width: '100%' },
        }}
        action={<ArrowForward color="info" sx={{ marginTop: 0.5 }} />}
      >
        Looking to send notifications to a custom email address, such as an
        email list or a third party? Set up custom notifications.
      </Alert>
    </RouterLink>
  );
}

function EventSubscriptionsCard() {
  const catalog = useMessageTypes();
  const subscriptions = useNotificationSubscriptions();
  const { saveSubscriptions } = useSaveNotificationSubscriptions();
  // What the server says is derived, not stored: an edit is the only thing
  // this screen owns, and null says the caller has not made one. A newer answer
  // therefore shows the moment it lands, and an edit made while a save was in
  // flight is the caller's and survives it.
  const [edit, setEdit] = useState<SubscriptionDraft | null>(null);
  const saved = toDraft(subscriptions.data?.typeSubscriptions ?? []);
  const draft = edit ?? saved;

  const isDirty = edit !== null && !haveSameChannels(draft, saved);
  const entries = catalog.data ?? [];
  const groups = toGroupedEntries(entries);

  const handleToggle = (messageType: string, channel: MessageChannel) => {
    setEdit((current) => {
      const next = new Map(current ?? saved);
      const channels = new Set(next.get(messageType) ?? []);
      if (channels.has(channel)) {
        channels.delete(channel);
      } else {
        channels.add(channel);
      }
      next.set(messageType, channels);
      return next;
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // The route replaces only within the types this screen says it offered, so
    // a save before the catalog arrives changes nothing rather than reading an
    // empty answer as unsubscribe from everything
    const offeredMessageTypes = entries.map((entry) => entry.messageType);
    // The route takes the complete set, and a type with no channels is left out
    // rather than sent empty. Only what the catalog lists goes back, because a
    // stored row for a type this screen never drew would fail validation and
    // the caller would have no way to clear it
    const listedTypes = new Set(offeredMessageTypes);
    const payload = [...draft.entries()]
      .filter(([messageType, channels]) => {
        return channels.size > 0 && listedTypes.has(messageType);
      })
      .map(([messageType, channels]) => ({
        messageType,
        channels: [...channels],
      }));
    // Cleared on the answer rather than on the click, so the switches keep
    // saying what was sent until the server says it back
    saveSubscriptions.mutate(
      { offeredMessageTypes, subscriptions: payload },
      { onSuccess: () => setEdit(null) }
    );
  };

  const isLoading = catalog.isPending || subscriptions.isPending;
  const isError = catalog.isError || subscriptions.isError;

  return (
    <Card variant="outlined" component="form" onSubmit={handleSubmit}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        paddingX={2}
        paddingY={1.5}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Event subscriptions
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={() => setEdit(null)}
            disabled={!isDirty || saveSubscriptions.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="small"
            variant="contained"
            disabled={
              !isDirty || saveSubscriptions.isPending || entries.length === 0
            }
          >
            Save
          </Button>
        </Stack>
      </Stack>
      <Divider />
      {isError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Notification settings could not be loaded
        </Alert>
      )}
      {saveSubscriptions.isError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Those subscriptions could not be saved
        </Alert>
      )}
      {isLoading ? (
        <Stack alignItems="center" padding={5}>
          <CircularProgress />
        </Stack>
      ) : entries.length === 0 ? (
        <Stack alignItems="center" padding={5}>
          <Typography variant="body2" color="text.secondary">
            There are no events to subscribe to yet
          </Typography>
        </Stack>
      ) : (
        <>
          <Box
            display="grid"
            gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
            alignItems="center"
            columnGap={2}
            paddingX={2}
            paddingY={COLUMN_HEADING_PADDING_Y}
            bgcolor="grey.50"
            borderBottom={1}
            borderColor="divider"
            sx={{
              '& .MuiTypography-root': {
                color: 'text.secondary',
                fontSize: COLUMN_HEADING_FONT_SIZE,
                fontWeight: 600,
                letterSpacing: 0.5,
                lineHeight: 1,
                textTransform: 'uppercase',
              },
            }}
          >
            <Typography>Event</Typography>
            {CHANNEL_COLUMNS.map((channel) => (
              <Typography key={channel} align="center">
                {toMessageChannelLabel(channel)}
              </Typography>
            ))}
          </Box>
          <CardContent>
            <Box
              display="grid"
              gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
              alignItems="center"
              columnGap={2}
              rowGap={1}
            >
              {groups.map((group, groupIndex) => (
                <Fragment key={group.label}>
                  {groupIndex > 0 && (
                    <Divider
                      sx={{ gridColumn: '1 / -1', marginTop: 1, marginX: -2 }}
                    />
                  )}
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ gridColumn: '1 / -1' }}
                  >
                    {group.label}
                  </Typography>
                  {group.entries.map((entry) => (
                    <EventSubscriptionRow
                      key={entry.messageType}
                      entry={entry}
                      channels={draft.get(entry.messageType) ?? new Set()}
                      onToggle={handleToggle}
                    />
                  ))}
                </Fragment>
              ))}
            </Box>
          </CardContent>
        </>
      )}
    </Card>
  );
}

interface EventSubscriptionRowProps {
  entry: MessageTypeCatalogEntry;
  channels: Set<MessageChannel>;
  onToggle: (messageType: string, channel: MessageChannel) => void;
}

function EventSubscriptionRow(props: EventSubscriptionRowProps) {
  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1.5} paddingY={0.5}>
        <Stack>
          <Typography variant="body2" fontWeight={600}>
            {props.entry.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {props.entry.description}
          </Typography>
        </Stack>
        <Box
          flex={1}
          sx={{
            alignSelf: 'flex-end',
            borderBottom: '1px dotted',
            borderColor: 'divider',
            marginBottom: 0.75,
          }}
        />
      </Stack>
      {CHANNEL_COLUMNS.map((channel) => (
        <ChannelSwitch
          key={channel}
          entry={props.entry}
          channel={channel}
          isOn={props.channels.has(channel)}
          onToggle={props.onToggle}
        />
      ))}
    </>
  );
}

interface ChannelSwitchProps {
  entry: MessageTypeCatalogEntry;
  channel: MessageChannel;
  isOn: boolean;
  onToggle: (messageType: string, channel: MessageChannel) => void;
}

function ChannelSwitch(props: ChannelSwitchProps) {
  const label = `${props.entry.label} ${toMessageChannelLabel(props.channel)}`;
  // Every event keeps its place in every column, so the rows stay readable. A
  // channel the event does not declare is refused by the backend, so the switch
  // is there and cannot be moved
  const isSupported = props.entry.channels.includes(props.channel);

  if (!isSupported) {
    return (
      <Tooltip arrow title="Coming soon">
        <Box component="span" sx={{ justifySelf: 'center' }}>
          <Switch disabled size="small" inputProps={{ 'aria-label': label }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Switch
      size="small"
      checked={props.isOn}
      onChange={() => props.onToggle(props.entry.messageType, props.channel)}
      inputProps={{ 'aria-label': label }}
      sx={{ justifySelf: 'center' }}
    />
  );
}

function RecordNotificationsCard() {
  const subscriptions = useNotificationSubscriptions();
  const { saveEntityWatches } = useSaveNotificationSubscriptions();
  // Staging a turn-off is the only thing this screen owns, so it holds the
  // keys turned off rather than a copy of the list. A watch that arrives or
  // disappears between reads needs no reconciling: a staged key that names no
  // listed row matches nothing.
  const [stagedOff, setStagedOff] = useState<Set<string>>(new Set());

  const listed = subscriptions.data?.entityWatches ?? [];
  const isDirty = listed.some((watch) => stagedOff.has(toWatchKey(watch)));

  const handleToggleRow = (watchKey: string) => {
    setStagedOff((current) => {
      const next = new Set(current);
      if (next.has(watchKey)) {
        next.delete(watchKey);
      } else {
        next.add(watchKey);
      }
      return next;
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Each record is its own route, so the mutation walks them in order rather
    // than racing several answers into one cache entry
    const turnedOff = listed
      .filter((watch) => stagedOff.has(toWatchKey(watch)))
      .map((watch) => ({
        entityType: watch.entityType,
        entityId: watch.entityId,
        channels: [],
      }));
    // Cleared on the answer rather than on the click, so a row stays struck
    // through until the server has dropped it
    saveEntityWatches.mutate(turnedOff, {
      onSuccess: () => setStagedOff(new Set()),
    });
  };

  return (
    <Card variant="outlined" component="form" onSubmit={handleSubmit}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        paddingX={2}
        paddingY={1.5}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Record notifications
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={() => setStagedOff(new Set())}
            disabled={!isDirty || saveEntityWatches.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="small"
            variant="contained"
            disabled={!isDirty || saveEntityWatches.isPending}
          >
            Save
          </Button>
        </Stack>
      </Stack>
      <Divider />
      {saveEntityWatches.isError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Those records could not be updated
        </Alert>
      )}
      <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
        {subscriptions.isPending ? (
          <Stack alignItems="center" padding={5}>
            <CircularProgress />
          </Stack>
        ) : listed.length === 0 ? (
          <Stack alignItems="center" padding={5}>
            <Typography variant="body2" color="text.secondary">
              Notifications are not turned on for any records &mdash; turn them
              on from a record&apos;s page
            </Typography>
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Record type</TableCell>
                <TableCell>Record name</TableCell>
                <TableCell>Notifications since</TableCell>
                <TableCell align="right" width={120}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listed.map((watch) => (
                <RecordNotificationRow
                  key={toWatchKey(watch)}
                  watch={watch}
                  isPendingTurnOff={stagedOff.has(toWatchKey(watch))}
                  onToggle={handleToggleRow}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

interface RecordNotificationRowProps {
  watch: EntityWatch;
  isPendingTurnOff: boolean;
  onToggle: (entityId: string) => void;
}

function RecordNotificationRow(props: RecordNotificationRowProps) {
  return (
    <TableRow
      hover={!props.isPendingTurnOff}
      sx={{ opacity: props.isPendingTurnOff ? 0.5 : 1 }}
    >
      <TableCell>
        <StatusChip
          label={toEntityTypeLabel(props.watch.entityType)}
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <RecordName watch={props.watch} />
      </TableCell>
      <TableCell>
        <MultiTimezoneTooltip timestamp={props.watch.watchingSince}>
          <Typography
            component="span"
            variant="body2"
            sx={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}
          >
            {toWatchingSinceLabel(props.watch.watchingSince)}
          </Typography>
        </MultiTimezoneTooltip>
      </TableCell>
      <TableCell align="right" width={120} sx={{ whiteSpace: 'nowrap' }}>
        <Button
          size="small"
          sx={{ minWidth: 80 }}
          onClick={() => props.onToggle(toWatchKey(props.watch))}
        >
          {props.isPendingTurnOff ? 'Turn on' : 'Turn off'}
        </Button>
      </TableCell>
    </TableRow>
  );
}

/** Identifies one watch, which an id alone would not across record types. */
function toWatchKey(watch: EntityWatch): string {
  return `${watch.entityType}-${watch.entityId}`;
}

interface RecordNameProps {
  watch: EntityWatch;
}

function RecordName(props: RecordNameProps) {
  // A watch outlives the record it points at, and the row still has to offer a
  // way to turn it off
  if (props.watch.entity === null) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No longer available
      </Typography>
    );
  }
  return <Typography variant="body2">{props.watch.entity.name}</Typography>;
}

/** Renders the date a watch started. */
function toWatchingSinceLabel(watchingSince: DateTime): string {
  return watchingSince.toLocaleString(MEDIUM_DATE_FORMAT);
}

/** Groups catalog entries under the heading each one names. */
function toGroupedEntries(entries: MessageTypeCatalogEntry[]) {
  const byGroup = new Map<string, MessageTypeCatalogEntry[]>();
  for (const entry of entries) {
    const group = byGroup.get(entry.group) ?? [];
    group.push(entry);
    byGroup.set(entry.group, group);
  }
  return [...byGroup.entries()].map(([label, groupEntries]) => ({
    label,
    entries: groupEntries,
  }));
}

/** Turns the caller's saved subscriptions into an editable draft. */
function toDraft(
  subscriptions: { messageType: string; channels: MessageChannel[] }[]
): SubscriptionDraft {
  return new Map(
    subscriptions.map((subscription) => [
      subscription.messageType,
      new Set(subscription.channels),
    ])
  );
}

/** Reports whether two drafts hold the same channels for the same types. */
function haveSameChannels(left: SubscriptionDraft, right: SubscriptionDraft) {
  const messageTypes = new Set([...left.keys(), ...right.keys()]);
  for (const messageType of messageTypes) {
    const leftChannels = left.get(messageType) ?? new Set<MessageChannel>();
    const rightChannels = right.get(messageType) ?? new Set<MessageChannel>();
    if (leftChannels.size !== rightChannels.size) {
      return false;
    }
    for (const channel of leftChannels) {
      if (!rightChannels.has(channel)) {
        return false;
      }
    }
  }
  return true;
}
